import React, { useRef, useState, ReactNode } from "react";
import { ServiceCustomFormsMaximumFile } from "@/apis/clients";
import ClearIcon from "public/icon/clear.svg";
import { fileApi } from "@/apis/file/fileApi";
import { fileToBase64 } from "@/utils/common";
import { useLoading } from "@/states/loading/operations";

export interface IFileUpload {
  id: string;
  file: File;
}

interface CustomFormUploadFileProps {
  value?: IFileUpload[]
  acceptFile: string;
  maximumFile?: ServiceCustomFormsMaximumFile;
  fileTypeLabel: string;
  onChangeFile?: (item: IFileUpload[]) => void;
}

interface UploadedFile {
  file: File;
  id: string;
}

export function CustomFormUploadFile({
  value,
  acceptFile,
  maximumFile = ServiceCustomFormsMaximumFile.SINGLE,
  fileTypeLabel,
  onChangeFile
}: CustomFormUploadFileProps) {
  const withLoading = useLoading();

  const maxFileSize = maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
  const [errorMessage, setErrorMessage] = useState<
    ReactNode | string | undefined
  >(undefined);

  const clearError = () => {
    setErrorMessage(undefined);
  };
  const [files, setFiles] = useState<UploadedFile[]>(value && value.length ? value: []);

  const handleUpload = async (newFiles: File[]) => {
    await withLoading(async () => {
      clearError();

      const totalFilesCount = files.length + newFiles.length;
      if (totalFilesCount > 5) {
        setErrorMessage(`ファイルは5つまでアップロードです`);
        return;
      }
      const acceptedExtensions = acceptFile
      .split(',')
      .map((ext) => ext.trim().toLowerCase().replace('.', ''))
      .filter(Boolean);
      for (const file of newFiles) {
        if (file.size > maxFileSize) {
          setErrorMessage(`${maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? 10 : 5}MBより大きいファイルはアップロードできません`);
          return;
        }
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        if (!fileExt || !acceptedExtensions.includes(fileExt)) {
          setErrorMessage(`ファイルは${acceptedExtensions.join(",").toUpperCase()}形式でお願いします`);
          return;
        }
      }
    
      if (
        maximumFile === ServiceCustomFormsMaximumFile.MULTIPLE &&
        totalFilesCount > 5
      ) {
        setErrorMessage(`ファイルは5つまでアップロードです`);
        return;
      }
    
      const uploadResponses: UploadedFile[] = [];
      await new Promise((resolve) => setTimeout(resolve, 0));
      for (const file of newFiles) {
        try {
          const base64 = await fileToBase64(file);
    
          const response = await fileApi.postUploadFile({
            postUploadFileRequest: { name: file.name, file: base64 },
          });
    
          uploadResponses.push({
            file,
            id: response.data.id,
          });
        } catch (error) {
          setErrorMessage(`${file.name} のアップロードに失敗しました`);
          return;
        }
      }
    
      const updatedFiles = [...files, ...uploadResponses];
      setFiles(updatedFiles);
    
      if (onChangeFile) {
        onChangeFile(updatedFiles);
      }
    
      clearError();
    });
  };

  const handleRemoveFile = (index: number) => {
    clearError();
    const combinedFiles = files.filter((_, i) => i !== index);
    setFiles(combinedFiles);
    onChangeFile && onChangeFile(combinedFiles);
  };
  
  const max = maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? 1 : 5;

  return (
    <>
      <div className="w-full h-full p-4 relative bg-theme-thin rounded">
        <div className="text-center md:py-2">
          <div className="md:flex md:justify-center items-center gap-2 mb-2">
            <UploadButton
              onUpload={(files: File[]) => void handleUpload(files)}
              accept={acceptFile}
              multiple={maximumFile === ServiceCustomFormsMaximumFile.MULTIPLE}
              disabled={maximumFile === ServiceCustomFormsMaximumFile.MULTIPLE ? files.length >= 5 : files.length === 1}
            >
              ファイルを選択
            </UploadButton>
            <p className="text-theme-medium">
              {`${maximumFile === ServiceCustomFormsMaximumFile.SINGLE ? "単一" : "複数"}選択可（${files.length}/${max}ファイル）`}
            </p>
          </div>
          <p className="text-theme-medium">{fileTypeLabel}</p>
        </div>
      </div>
      {errorMessage && <p className="mt-1 text-accent break-all">{errorMessage}</p>}
      {files.length > 0 && (
        <ul className="flex items-center flex-wrap gap-2 mt-2">
          {files.map((file, index) => (
            <li
              key={index}
              className="flex max-w-full break-all items-center rounded-2xl border border-solid border-color-green-100 py-1 pl-4 pr-2 gap-2"
            >
              <span className="text-xs font-bold text-color-green-100">{file.file.name}</span>
              <ClearIcon className="cursor-pointer shrink-0" onClick={() => handleRemoveFile(index)} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// --- UploadButton Component ---
interface ButtonProps {
  onUpload: (files: File[]) => void;
  children: ReactNode;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
}

function UploadButton({
  onUpload,
  children,
  accept,
  multiple = true,
  disabled = false,
}: ButtonProps) {
  const inputFileRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        type="button"
        className={`border px-3 py-1 rounded text-sm font-medium ${
          disabled
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-white border-primary text-primary hover:bg-blue-50"
        }`}
        disabled={disabled}
        onClick={() => inputFileRef.current?.click()}
      >
        {children}
      </button>

      <input
        ref={inputFileRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={({ target }) => {
          const files = target.files ? Array.from(target.files) : [];
          onUpload(files);
          target.value = ""; // reset input to allow re-upload same file
        }}
      />
    </>
  );
}
